import Logger from './logger';
import ErrorTracker from './errorTracker';
import securityHeadersService from './securityHeadersService';
import { REGISTRATION_TURNSTILE_CSP_ORIGIN } from '../constants/registrationAbuse';
import { LOCAL_DEV_CONNECT_SRC } from '../constants/localDevOrigins';
import {
  FIREBASE_APP_CHECK_CSP_CONNECT_ORIGINS,
  FIREBASE_APP_CHECK_CSP_FRAME_ORIGINS,
  FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS,
} from '../constants/appCheck';

/** Dev-only loopback CSP connect-src entries — stripped from production bundles at build time. */
const DEV_LOCALHOST_CONNECT_CSP_SOURCES: string[] =
  process.env.NODE_ENV !== 'production' ? [...LOCAL_DEV_CONNECT_SRC] : [];

// CSP configuration
interface CSPConfig {
  mode: 'enforce' | 'report-only' | 'both';
  reportUri?: string;
  enableNonce: boolean;
  enableStrictDynamic: boolean;
  enableUnsafeInline: boolean;
  enableUnsafeEval: boolean;
  customDirectives: Record<string, string[]>;
  environment: 'development' | 'production' | 'staging';
}

// CSP violation
interface CSPViolation {
  id: string;
  timestamp: number;
  blockedURI: string;
  violatedDirective: string;
  effectiveDirective: string;
  originalPolicy: string;
  referrer: string;
  statusCode: number;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  resolvedAt?: number;
  resolvedBy?: string;
}

// CSP statistics
interface CSPStats {
  totalViolations: number;
  violationsByDirective: Record<string, number>;
  violationsBySource: Record<string, number>;
  violationsBySeverity: Record<string, number>;
  recentViolations: CSPViolation[];
  averageViolationsPerDay: number;
  lastViolation: CSPViolation | null;
}

// CSP directive
interface CSPDirectiveConfig {
  directive: string;
  sources: string[];
  required: boolean;
  environmentSpecific: boolean;
  developmentSources?: string[];
  productionSources?: string[];
}

class CSPService {
  private static instance: CSPService;
  private config: CSPConfig;
  private violations: CSPViolation[] = [];
  private stats: CSPStats;
  private isInitialized: boolean = false;
  private violationListeners: ((violation: CSPViolation) => void)[] = [];

  // Comprehensive CSP directives for different environments
  private readonly directiveConfigs: CSPDirectiveConfig[] = [
    {
      directive: 'default-src',
      sources: ["'self'"],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'script-src',
      sources: ["'self'", "'unsafe-inline'", "'unsafe-eval'", "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "https://js.stripe.com", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS, ""],
      developmentSources: ["'self'", "'unsafe-inline'", "'unsafe-eval'", "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "https://www.googletagmanager.com", "https://cdn.jsdelivr.net", "https://js.stripe.com", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS, ""],
      productionSources: ["'self'", "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "https://www.googletagmanager.com", "https://js.stripe.com", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_SCRIPT_ORIGINS, ""],
      required: true,
      environmentSpecific: true
    },
    {
      directive: 'style-src',
      sources: ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
      developmentSources: ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com", "https://cdn.jsdelivr.net"],
      productionSources: ["'self'", "https://fonts.googleapis.com"],
      required: true,
      environmentSpecific: true
    },
    {
      directive: 'font-src',
      sources: ["'self'", "https://fonts.gstatic.com"],
      developmentSources: ["'self'", "https://fonts.gstatic.com", "https://cdn.jsdelivr.net"],
      productionSources: ["'self'", "https://fonts.gstatic.com"],
      required: true,
      environmentSpecific: true
    },
    {
      directive: 'img-src',
      sources: ["'self'", "data:", "https:", "https://*.tranzit.io", "https://*.firebase.com"],
      developmentSources: ["'self'", "data:", "https:", "https://*.tranzit.io", "https://*.firebase.com", "https://*.mapbox.com"],
      productionSources: ["'self'", "data:", "https:", "https://*.tranzit.io", "https://*.firebase.com", "https://*.mapbox.com"],
      required: true,
      environmentSpecific: true
    },
    {
      directive: 'connect-src',
      sources: ["'self'", "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "https://*.cloudfunctions.net", "https://securetoken.googleapis.com", "https://identitytoolkit.googleapis.com", "https://*.googleapis.com"],
      developmentSources: ["'self'", ...DEV_LOCALHOST_CONNECT_CSP_SOURCES, "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "https://*.cloudfunctions.net", "https://securetoken.googleapis.com", "https://identitytoolkit.googleapis.com", "https://*.googleapis.com", "https://www.google-analytics.com", "https://www.googletagmanager.com", "https://events.mapbox.com", "https://api.quickbooks.com", "https://www.routingnumbers.info", "", "https://api.stripe.com", "https://api.paypal.com", "https://api.squareup.com", "https://api.zelle.com", "wss://*.firebase.com", "wss://*.firebaseio.com", "https://api.mapbox.com", "https://mobile.fmcsa.dot.gov", "https://corsproxy.io", "https://api.allorigins.win", "https://cors-anywhere.herokuapp.com", "https://safer.fmcsa.dot.gov", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_CONNECT_ORIGINS],
      // Production sources - NO CORS proxy services, NO localhost
      productionSources: ["'self'", "https://*.tranzit.io", "https://*.run.app", ...(process.env.REACT_APP_BACKEND_URL ? [process.env.REACT_APP_BACKEND_URL] : []), "https://*.firebase.com", "https://*.firebaseio.com", "https://*.cloudfunctions.net", "https://securetoken.googleapis.com", "https://identitytoolkit.googleapis.com", "https://*.googleapis.com", "https://www.google-analytics.com", "https://www.googletagmanager.com", "https://api.quickbooks.com", "https://www.routingnumbers.info", "", "https://api.stripe.com", "https://api.paypal.com", "https://api.squareup.com", "https://api.zelle.com", "wss://*.firebase.com", "wss://*.firebaseio.com", "https://api.mapbox.com", "https://mobile.fmcsa.dot.gov", "https://safer.fmcsa.dot.gov", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_CONNECT_ORIGINS],
      required: true,
      environmentSpecific: true
    },
    {
      directive: 'frame-src',
      sources: ["'self'", "https://*.tranzit.io", "https://*.firebase.com", "https://*.firebaseio.com", "", "https://js.stripe.com", "https://www.paypal.com", "https://squareup.com", REGISTRATION_TURNSTILE_CSP_ORIGIN, ...FIREBASE_APP_CHECK_CSP_FRAME_ORIGINS],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'worker-src',
      sources: ["'self'", "blob:", "https://*.firebase.com", "https://*.firebaseio.com"],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'object-src',
      sources: ["'none'"],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'media-src',
      sources: ["'self'", "https:"],
      required: false,
      environmentSpecific: false
    },
    {
      directive: 'worker-src',
      sources: ["'self'", "blob:"],
      required: false,
      environmentSpecific: false
    },
    {
      directive: 'child-src',
      sources: ["'self'"],
      required: false,
      environmentSpecific: false
    },
    {
      directive: 'form-action',
      sources: ["'self'"],
      required: false,
      environmentSpecific: false
    },
    {
      directive: 'frame-ancestors',
      sources: ["'none'"],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'base-uri',
      sources: ["'self'"],
      required: true,
      environmentSpecific: false
    },
    {
      directive: 'upgrade-insecure-requests',
      sources: [],
      required: false,
      environmentSpecific: false
    }
  ];

  constructor() {
    this.config = {
      mode: 'enforce',
      enableNonce: false,
      enableStrictDynamic: false,
      enableUnsafeInline: true,
      enableUnsafeEval: true,
      environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
      customDirectives: {}
    };

    this.stats = {
      totalViolations: 0,
      violationsByDirective: {},
      violationsBySource: {},
      violationsBySeverity: {},
      recentViolations: [],
      averageViolationsPerDay: 0,
      lastViolation: null
    };
  }

  public static getInstance(): CSPService {
    if (!CSPService.instance) {
      CSPService.instance = new CSPService();
    }
    return CSPService.instance;
  }

  /**
   * Initialize CSP service
   */
  public initialize(config: Partial<CSPConfig> = {}): void {
    try {
      this.config = { ...this.config, ...config };
      this.setupCSPViolationReporting();
      this.updateCSPPolicy();
      this.isInitialized = true;

      Logger.info('CSP Service initialized', {
        mode: this.config.mode,
        environment: this.config.environment,
        directives: this.directiveConfigs.length
      });

      ErrorTracker.trackDataProtectionEvent(
        'csp_service_initialized',
        'internal',
        undefined,
        { mode: this.config.mode, environment: this.config.environment }
      );

    } catch (error) {
      Logger.error('Failed to initialize CSP Service', error instanceof Error ? error : new Error('Unknown error'));
      ErrorTracker.trackError(
        error instanceof Error ? error : new Error('CSP Service initialization failed'),
        'CSPService',
        { component: 'CSPService', action: 'initialize' }
      );
    }
  }

  /**
   * Update CSP policy
   */
  public updateCSPPolicy(): void {
    try {
      const cspValue = this.generateCSPValue();
      
      // Update security headers service
      securityHeadersService.updateConfig({
        enableCSP: true,
        cspReportOnly: this.config.mode === 'report-only'
      });

      // Apply CSP via meta tag
      this.applyCSPMetaTag(cspValue);

      Logger.info('CSP policy updated', { cspValue });

    } catch (error) {
      Logger.error('Failed to update CSP policy', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Generate CSP value
   */
  private generateCSPValue(): string {
    const directives: string[] = [];

    for (const directiveConfig of this.directiveConfigs) {
      if (!directiveConfig.required && !this.config.customDirectives[directiveConfig.directive]) {
        continue;
      }

      let sources = directiveConfig.sources;

      // Use environment-specific sources if available
      if (directiveConfig.environmentSpecific) {
        if (this.config.environment === 'development' && directiveConfig.developmentSources) {
          sources = directiveConfig.developmentSources;
        } else if (this.config.environment === 'production' && directiveConfig.productionSources) {
          sources = directiveConfig.productionSources;
        }
      }

      // Apply custom directives if provided
      if (this.config.customDirectives[directiveConfig.directive]) {
        sources = this.config.customDirectives[directiveConfig.directive];
      }

      // Add nonce if enabled
      if (this.config.enableNonce && (directiveConfig.directive === 'script-src' || directiveConfig.directive === 'style-src')) {
        sources.push("'nonce-" + this.generateNonce() + "'");
      }

      // Add strict-dynamic if enabled
      if (this.config.enableStrictDynamic && directiveConfig.directive === 'script-src') {
        sources.push("'strict-dynamic'");
      }

      if (sources.length > 0) {
        directives.push(`${directiveConfig.directive} ${sources.join(' ')}`);
      } else {
        directives.push(directiveConfig.directive);
      }
    }

    let cspValue = directives.join('; ');

    // Add report-uri if configured
    if (this.config.reportUri) {
      cspValue += `; report-uri ${this.config.reportUri}`;
    }

    return cspValue;
  }

  /**
   * Apply CSP via meta tag
   */
  private applyCSPMetaTag(cspValue: string): void {
    try {
      // Remove existing CSP meta tags
      const existingMetaTags = document.querySelectorAll('meta[http-equiv*="Content-Security-Policy"]');
      existingMetaTags.forEach(tag => tag.remove());

      // Create new CSP meta tag
      const meta = document.createElement('meta');
      meta.setAttribute('http-equiv', 'Content-Security-Policy');
      meta.setAttribute('content', cspValue);
      
      // Add to document head
      document.head.appendChild(meta);

      Logger.info('CSP meta tag applied', { cspValue });

    } catch (error) {
      Logger.error('Failed to apply CSP meta tag', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Set up CSP violation reporting
   */
  private setupCSPViolationReporting(): void {
    try {
      document.addEventListener('securitypolicyviolation', (event) => {
        const violation = this.createViolationFromEvent(event);
        this.recordViolation(violation);
        this.notifyViolationListeners(violation);
      });

      Logger.info('CSP violation reporting set up');

    } catch (error) {
      Logger.error('Failed to set up CSP violation reporting', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Create violation from event
   */
  private createViolationFromEvent(event: SecurityPolicyViolationEvent): CSPViolation {
    const severity = this.determineViolationSeverity(event);
    
    return {
      id: `csp_violation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      effectiveDirective: event.effectiveDirective,
      originalPolicy: event.originalPolicy,
      referrer: event.referrer,
      statusCode: event.statusCode,
      sourceFile: (event as any).sourceFile,
      lineNumber: (event as any).lineNumber,
      columnNumber: (event as any).columnNumber,
      severity,
      resolved: false
    };
  }

  /**
   * Determine violation severity
   */
  private determineViolationSeverity(event: SecurityPolicyViolationEvent): 'low' | 'medium' | 'high' | 'critical' {
    const blockedURI = event.blockedURI;
    const violatedDirective = event.violatedDirective;

    // Critical: Script injection attempts
    if (violatedDirective.includes('script-src') && blockedURI.includes('javascript:')) {
      return 'critical';
    }

    // High: External script/style loads
    if ((violatedDirective.includes('script-src') || violatedDirective.includes('style-src')) && 
        blockedURI.startsWith('http') && !blockedURI.includes('tranzit.io') && !blockedURI.includes('firebase.com')) {
      return 'high';
    }

    // Medium: Image or media loads
    if ((violatedDirective.includes('img-src') || violatedDirective.includes('media-src')) && 
        blockedURI.startsWith('http')) {
      return 'medium';
    }

    // Low: Everything else
    return 'low';
  }

  /**
   * Record violation
   */
  private recordViolation(violation: CSPViolation): void {
    try {
      this.violations.push(violation);
      this.updateStats(violation);

      Logger.warn('CSP violation recorded', {
        id: violation.id,
        directive: violation.violatedDirective,
        blockedURI: violation.blockedURI,
        severity: violation.severity
      });

      ErrorTracker.trackDataProtectionEvent(
        'csp_violation_recorded',
        violation.severity,
        undefined,
        {
          directive: violation.violatedDirective,
          blockedURI: violation.blockedURI,
          severity: violation.severity
        }
      );

    } catch (error) {
      Logger.error('Failed to record CSP violation', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Update statistics
   */
  private updateStats(violation: CSPViolation): void {
    this.stats.totalViolations++;
    
    // Update violations by directive
    this.stats.violationsByDirective[violation.violatedDirective] = 
      (this.stats.violationsByDirective[violation.violatedDirective] || 0) + 1;
    
    // Update violations by source
    const source = violation.blockedURI.split('/')[2] || 'unknown';
    this.stats.violationsBySource[source] = (this.stats.violationsBySource[source] || 0) + 1;
    
    // Update violations by severity
    this.stats.violationsBySeverity[violation.severity] = 
      (this.stats.violationsBySeverity[violation.severity] || 0) + 1;
    
    // Update recent violations
    this.stats.recentViolations.unshift(violation);
    if (this.stats.recentViolations.length > 100) {
      this.stats.recentViolations = this.stats.recentViolations.slice(0, 100);
    }
    
    // Update last violation
    this.stats.lastViolation = violation;
    
    // Calculate average violations per day
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const recentViolations = this.violations.filter(v => v.timestamp > oneDayAgo);
    this.stats.averageViolationsPerDay = recentViolations.length;
  }

  /**
   * Notify violation listeners
   */
  private notifyViolationListeners(violation: CSPViolation): void {
    this.violationListeners.forEach(listener => {
      try {
        listener(violation);
      } catch (error) {
        Logger.error('Error in CSP violation listener', error instanceof Error ? error : new Error('Unknown error'));
      }
    });
  }

  /**
   * Generate nonce
   */
  private generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Add violation listener
   */
  public addViolationListener(listener: (violation: CSPViolation) => void): void {
    this.violationListeners.push(listener);
  }

  /**
   * Remove violation listener
   */
  public removeViolationListener(listener: (violation: CSPViolation) => void): void {
    const index = this.violationListeners.indexOf(listener);
    if (index > -1) {
      this.violationListeners.splice(index, 1);
    }
  }

  /**
   * Get violations
   */
  public getViolations(filter?: {
    severity?: string;
    directive?: string;
    resolved?: boolean;
    startTime?: number;
    endTime?: number;
  }): CSPViolation[] {
    let filteredViolations = [...this.violations];

    if (filter) {
      if (filter.severity) {
        filteredViolations = filteredViolations.filter(v => v.severity === filter.severity);
      }
      if (filter.directive) {
        filteredViolations = filteredViolations.filter(v => v.violatedDirective === filter.directive);
      }
      if (filter.resolved !== undefined) {
        filteredViolations = filteredViolations.filter(v => v.resolved === filter.resolved);
      }
      if (filter.startTime) {
        filteredViolations = filteredViolations.filter(v => v.timestamp >= filter.startTime!);
      }
      if (filter.endTime) {
        filteredViolations = filteredViolations.filter(v => v.timestamp <= filter.endTime!);
      }
    }

    return filteredViolations.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Get statistics
   */
  public getStats(): CSPStats {
    return { ...this.stats };
  }

  /**
   * Resolve violation
   */
  public resolveViolation(violationId: string, resolvedBy: string): boolean {
    const violation = this.violations.find(v => v.id === violationId);
    if (violation) {
      violation.resolved = true;
      violation.resolvedAt = Date.now();
      violation.resolvedBy = resolvedBy;
      
      Logger.info('CSP violation resolved', { violationId, resolvedBy });
      return true;
    }
    return false;
  }

  /**
   * Clear violations
   */
  public clearViolations(): void {
    this.violations = [];
    this.stats = {
      totalViolations: 0,
      violationsByDirective: {},
      violationsBySource: {},
      violationsBySeverity: {},
      recentViolations: [],
      averageViolationsPerDay: 0,
      lastViolation: null
    };
    
    Logger.info('CSP violations cleared');
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<CSPConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.updateCSPPolicy();
    Logger.info('CSP configuration updated');
  }

  /**
   * Get current configuration
   */
  public getConfig(): CSPConfig {
    return { ...this.config };
  }

  /**
   * Check if service is initialized
   */
  public isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get directive configurations
   */
  public getDirectiveConfigs(): CSPDirectiveConfig[] {
    return [...this.directiveConfigs];
  }
}

// Export singleton instance
const cspService = new CSPService();
export default cspService;
export { type CSPConfig, type CSPViolation, type CSPStats, type CSPDirectiveConfig };
